<template>
  <v-tabs
      v-if="isTabsReady"
      v-model="selectedTab"
      centered
      :stacked="$device.isMobile"
      color="primary">
    <AtomsDefaultHeaderTab
        v-for="tab in tabs"
        :tabDetails="tab"
        :on-selected-tab-change="onSelectedItemChange"
        :alertIsDisabled="alertIsDisabled"/>
  </v-tabs>
  <v-overlay
      v-model="isExportingWorldStore"
      persistent
      class="align-center justify-center"
      scrim="#000">
    <v-progress-circular indeterminate color="primary" size="64"/>
  </v-overlay>
  <v-overlay
      v-model="isDownloadingPhotos"
      persistent
      class="align-center justify-center"
      scrim="#000">
    <v-progress-circular indeterminate color="primary" size="64"/>
  </v-overlay>
</template>
<script setup lang="ts">
import type {MenuItem, OriginalTab, TabDetails, TabUrl} from "~/types/Tab";
import {useI18n} from "vue-i18n";
import type {CrossDiag, Diagnostic} from "~/types/diagnostic";
import ArchiveDialog, {type ReturnDialogValue} from "~/components/molecules/dialogs/ArchiveDialog.vue";
import RestoreLastArchiveDialog, {type RestoreReturnDialogValue} from "~/components/atoms/RestoreLastArchiveDialog.vue";

const {$device} = useNuxtApp()
const selectedTab = ref<OriginalTab>()
const {t} = useI18n()
const kpiStore = useKpiStore()
const worldStore = useWorldStore()
const userStore = useUserStore()
const route = useRoute()
const decathlonStore = useDecathlonStores()
const diagnosticStore = useDiagnosticStore()
const {$open} = useNuxtApp()

const diagnostic = ref<TabDetails | undefined>({} as TabDetails)
const reporting = ref<TabDetails>({} as TabDetails)
const isExportingWorldStore = ref(false)
const isDownloadingPhotos = ref(false)

const isTabsReady = computed(() => {
  const value = diagnostic.value.items?.length > 0
  return value
})

const store = ref<TabDetails>({
  id: 'store',
  label: computed(() => t('my_store')),
  icon: 'mdi-home',
  items: [{label: computed(() => t('my_store')), icon: 'store-line', id: 'store' as TabUrl}],
  notif: undefined
})

watch(() => decathlonStore.selectedCrossDiag!, (crossDiag: CrossDiag) => {
  diagnostic.value = {
    id: 'diagnostic',
    icon: 'mdi-order-bool-ascending-variant',
    label: computed(() => t('diagnostic')),
    items: [
      {
        label: diagnosticStore.diagnostic?.rates.completion == 0 ? t(`begin-diag`) : t(`resume-diag`),
        icon: 'mdi-play-circle-outline',
        id: 'diagnostic-primary-theme' as TabUrl
      },
      ...(crossDiag.id == 1) ? [{
        label: t('custom-diag'),
        icon: 'mdi-cog-play-outline',
        id: 'diagnostic-custom-theme' as TabUrl
      }] : [],
      ...(crossDiag.id == 1) ? [{
        label: t('alert-diag'),
        icon: 'mdi-bug-play-outline',
        id: 'diagnostic-alert-theme' as TabUrl
      }] : [],
      {
        label: '',
        icon: '',
        id: 'separator' as TabUrl
      },
      {
        label: t('archive-diag'),
        icon: 'mdi-archive-arrow-down-outline',
        id: 'diagnostic-archive' as TabUrl,
        color: 'error'
      },
      {
        label: t('restore-last-archive.tab-item'),
        icon: 'mdi-backup-restore',
        id: 'restore-last-archive' as TabUrl,
        disabled: computed(() => diagnosticStore.diagnostic?.rates.completion != 0)
      },
      {
        label: t('historic-diag'),
        icon: 'mdi-history',
        id: 'diagnostic-history' as TabUrl,
        disabled: true
      }
    ]
  }
    }, {immediate: true}
)

const actionPlan = ref<TabDetails>({
  id: 'action-plan',
  label: computed(() => t('action-plan')),
  icon: 'mdi-calendar-check-outline',
  notif: undefined,
  items: [{label: computed(() => t('action-plan')), icon: 'calendar', id: 'action-plan' as TabUrl}]
})

watch(() => diagnosticStore.diagnostic, (value: Diagnostic | undefined) => actionPlan.value.notif = value?.perimeter.actionsStatus)

watch(() => decathlonStore.selectedCrossDiag!, (value: CrossDiag) => reporting.value = {
  id: 'reporting',
  icon: 'mdi-equalizer-outline',
  label: computed(() => t('reporting'))
  , items: [
    {label: computed(() => t('region')), icon: 'mdi-puzzle-outline', id: 'region' as TabUrl},
    {
      label: computed(() => t('my-country')), icon: 'mdi-map-outline', id: 'country' as TabUrl,
      subItems: [
        {label: computed(() => t('by_region')), icon: 'mdi-puzzle-outline', id: 'country-by-region' as TabUrl},
        {label: computed(() => t('by_store')), icon: 'mdi-home-group', id: 'country-by-store' as TabUrl}
      ]
    },
    ...(value?.countryScope === 'UNITED') ? [{
      label: computed(() => t('world')),
      icon: 'mdi-earth',
      id: 'world' as TabUrl
    }, ...(userStore.isAdmin) ? [{
      label: computed(() => t('world-store-export')),
      icon: 'mdi-download',
      id: 'world-store-export'
    }, {
      label: computed(() => t('photos-download')),
      icon: 'mdi-camera',
      id: 'photos-download'
    }] : []] : [],
  ]
}, {immediate: true})


const tabs = computed(() => {
  const reportingTab = {...reporting.value, items: reporting.value.items.filter(item => item.id === 'store')}
  const mobileTabs = [diagnostic.value, reportingTab]
  const desktopTabs = [store.value, diagnostic.value, actionPlan.value, reporting.value]
  return $device.isMobile ? mobileTabs : desktopTabs
})

const alertIsDisabled = (item: MenuItem) => item.id === 'diagnostic-alert-theme' && kpiStore.kpisOnAlert.length === 0

const onSelectedItemChange = async (item: MenuItem, originalTab: OriginalTab) => {
  selectedTab.value = originalTab
  switch (item.id) {
    case "store": {
      navigateTo(`/store`)
      break
    }
    case "action-plan": {
      navigateTo(`/action-plan`)
      break
    }
    case "region": {
      if (route.path.includes('/report/region/summary/byTheme')) {
        navigateTo(`/report/region/summary/byTheme`)
        break
      }
      if (route.path.includes('/report/region/summary/bySubTheme')) {
        navigateTo(`/report/region/summary/bySubTheme`)
        break
      }
      navigateTo(`/report/region/summary/byTheme`)
      break
    }
    case "country-by-region": {
      if (route.path.includes('/report/country/by-region/summary/bySubTheme')) {
        navigateTo(`/report/country/by-region/summary/bySubTheme`)
        break
      }
      navigateTo(`/report/country/by-region/summary/byTheme`)
      break
    }
    case "country-by-store": {
      if (route.path.includes('/report/country/by-store/summary/bySubTheme')) {
        navigateTo(`/report/country/by-store/summary/bySubTheme`)
        break
      }
      navigateTo(`/report/country/by-store/summary/byTheme`)
      break
    }
    case "world": {
      if (route.path.includes('/report/world/summary/bySubTheme')) {
        navigateTo(`/report/world/summary/bySubTheme`)
        break
      }
      navigateTo(`/report/world/summary/byTheme`)
      break
    }
    case "world-store-export": {
      isExportingWorldStore.value = true
      try {
        const success = await worldStore.fetchWorldStoreExport()
        if (success) {
          openToast('success', t('world-store-export-success'))
        }
      } catch (e) {
        openToast('error', t('world-store-export-error'))
      } finally {
        isExportingWorldStore.value = false
      }
      break
    }
    case "photos-download": {
      isDownloadingPhotos.value = true
      try {
        const success = await decathlonStore.downloadPhotosZip()
        if (success) {
          openToast('success', t('photos-download-success'))
        }
      } catch (e) {
        openToast('error', t('photos-download-error'))
      } finally {
        isDownloadingPhotos.value = false
      }
      break
    }
    case "diagnostic-alert-theme": {
      navigateTo(`/eval/alert/themes`)
      break
    }
    case "diagnostic-primary-theme": {
      route.params?.themeId && route.params?.diagType === 'primary' ? navigateTo(route.fullPath) : navigateTo(`/eval/primary/themes`)
      break
    }
    case "diagnostic-custom-theme": {
      route.params?.themeId && route.params?.diagType === 'custom' ? navigateTo(route.fullPath) : navigateTo(`/eval/custom/create`)
      break
    }
    case "diagnostic-archive": {
      const {toastType} = await $open<ReturnDialogValue>(ArchiveDialog, {
        scrim: true,
        isPersistent: true,
      })
      switch (toastType) {
        case 'success' : {
          openToast(toastType, t('archive.success-msg'))
          // await refresh()
          // questionSelected.value = []
          break
        }
        case 'error' : {
          openToast(toastType, t('archive.error-msg'))
          break
        }
        case 'canceled' :
          break
      }
      break
    }
      // case "diagnostic-history": {
      //   navigateTo(`/archive`)
      //   break
      // }
    case "restore-last-archive": {
      const {toastType} = await $open<RestoreReturnDialogValue>(RestoreLastArchiveDialog, {
        scrim: true,
        isPersistent: true,
      })
      switch (toastType) {
        case 'success' : {
          openToast(toastType, t('restore-last-archive.success-msg'))
          break
        }
        case 'error' : {
          openToast(toastType, t('restore-last-archive.error-msg'))
          break
        }
        case 'canceled' :
          break
      }
    }
  }
}

const tabRoutes: Record<TabUrl, string> = {
  'diagnostic-primary-theme': `/eval/primary/themes`,
  'diagnostic-alert-theme': `/eval/alert/themes`,
  'diagnostic-custom-theme': `/eval/custom/create`,
  'region': `/report/region/summary/byTheme`,
  'country-by-region': `/report/country/summary/byRegion/byTheme`,
  'country-by-store': `/report/country/summary/byStore/byTheme`,
  'world': `/report/world/summary/byTheme`,
  'store': `/store`,
  'action-plan': `/action-plan`,
//  'diagnostic-history': `/archive`,
}

const tabUrlToOriginalTab: Record<TabUrl, OriginalTab> = {
  'diagnostic-primary-theme': 'diagnostic',
  'diagnostic-alert-theme': 'diagnostic',
  'diagnostic-custom-theme': 'diagnostic',
//  'diagnostic-history': 'diagnostic',
  'store': 'reporting',
  'region': 'reporting',
  'country-by-region': `reporting`,
  'country-by-store': `reporting`,
  'world': 'reporting',
  'action-plan': 'action-plan',
};
const getTabFromRoutePath = (routePath: string): OriginalTab => {
  if (routePath.includes('/store')) return 'store'
  if (routePath.includes('/eval')) return 'diagnostic'
  if (routePath.includes('/action-plan/')) return 'action-plan'
  if (routePath.includes('/region')) return 'reporting'
  if (routePath.includes('/country')) return 'reporting'
  if (routePath.includes('/world/')) return 'reporting'


  const foundTabUrl = useFindKey(tabRoutes, path => routePath.includes(path)) as TabUrl
  return tabUrlToOriginalTab[foundTabUrl]
}


watch(() => route.path, value => {
  const path = value as string
  selectedTab.value = getTabFromRoutePath(path)
}, {immediate: true})

</script>
